import classNames from 'classnames';
import { memo, MouseEvent } from 'react';

import useDocumentCard from '../../hooks/useDocumentCard';
// import { FormattedMessage } from 'react-intl';
// import useFontSize from '../../hooks/useFontSize';
// import * as AppPropTypes from '../../lib/PropTypes';
import getRatio from '../../lib/getRatio';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import Category from '../partials/Category';
import DocumentCardMeta, { hasDocumentCardMeta } from '../partials/DocumentCardMeta';
import DocumentCardThumbnail from '../partials/DocumentCardThumbnail';
import Surtitle from '../partials/Surtitle';
import CardTitle from '../typography/CardTitle';
// import SponsorContent from '../typography/SponsorContent';
import VerticalCard from './VerticalCard';

import styles from '../../styles/cards/document-vertical-card.module.css';

export interface DocumentVerticalCardProps extends Omit<Partial<DocumentSnippet>, 'type'> {
    type: DocumentTypeString | 'native_ad' | null;
    externalUrl?: string | null;
    linkQuery?: Record<string, unknown> | null;
    ratio?: CardRatio;
    theme?: CardTheme | LegacyCardTheme | null;
    size?: CardSize;
    width?: number | null;
    imageLoading?: Loading;
    placeholder?: boolean;
    responsive?: boolean;
    withBorder?: boolean;
    withCategory?: boolean;
    withoutSurtitle?: boolean;
    withoutAuthor?: boolean;
    withoutAuthorImage?: boolean;
    withoutDuration?: boolean;
    withoutTime?: boolean;
    withBrandIcon?: boolean;
    withoutTypeIcon?: boolean;
    withoutBlockedIcon?: boolean;
    withoutSerie?: boolean;
    withoutCollection?: boolean;
    withEpisode?: boolean;
    withoutAdNotice?: boolean;
    className?: string | null;
    thumbnailClassName?: string | null;
    onClick?: ((event: MouseEvent<HTMLElement>) => void) | null;
}

const DocumentVerticalCard = ({
    id = null,
    type = null,
    slug = null,
    linkQuery = null,
    title = null,
    surtitle = null,
    brands = null,
    image = null,
    coverImage = null,
    categories = null,
    credits = null,
    authors = null,
    sponsors = null,
    topic = null,
    format = null,
    topics = null,
    formats = null,
    collection = null,
    duration = null,
    date = null,
    season = null,
    episode = null,
    micromagNumber = null,
    externalUrl = null,
    access = null,
    theme: providedTheme = null,
    size = null,
    ratio = null,
    width = null,
    placeholder = false,
    responsive = false,
    imageLoading = undefined,
    withBorder = false,
    withCategory = false,
    withoutSurtitle = false,
    withoutAuthor = false,
    withoutAuthorImage = false,
    withoutDuration = false,
    withoutTime = false,
    withBrandIcon = false,
    withoutTypeIcon = false,
    withoutBlockedIcon = false,
    withoutSerie = false,
    withoutCollection = false,
    withEpisode = false,
    withoutAdNotice = false,
    className = null,
    thumbnailClassName = null,
    onClick = null,
}: DocumentVerticalCardProps) => {
    const theme = normalizeCardTheme(providedTheme);
    const {
        href,
        category,
        author,
        brandHandle,
        sponsor,
        blocked,
        unblocked,
        progress,
        surtitle: finalSurtitle,
    } = useDocumentCard(
        {
            id,
            type,
            slug,
            externalUrl,
            surtitle,
            brands,
            categories,
            credits,
            authors,
            sponsors,
            topic,
            format,
            topics,
            formats,
            collection,
            season,
            episode,
            micromagNumber,
            date,
            access,
        },
        {
            linkQuery,
            placeholder,
            surtitle: {
                withoutSerie,
                withoutCollection,
                withCategory: !withCategory,
                withoutMicromagDate: true,
                withoutEpisode: !withEpisode,
            },
        },
    );

    const isNativeAd = type === 'native_ad';
    const isTiny = size === 'tiny';
    const isBox = theme !== null && theme.indexOf('box') === 0;
    const isMicromag = type === 'micromag';
    const isSuggestion = theme === 'suggestion';

    const defaultRatio = 5 / 4;
    // let defaultRatio = 1;
    // if (type === 'article') {
    //     defaultRatio = 5 / 4;
    // } else if (type === 'video') {
    //     defaultRatio = 4 / 5;
    // } else if (type === 'micromag') {
    //     defaultRatio = 4 / 6;
    // }

    const thumbnailRatio = getRatio(ratio, defaultRatio);

    const showCategory = withCategory && !isSuggestion && !isMicromag && !isTiny;
    const showSurtitle = !withoutSurtitle && !isSuggestion && !isTiny;
    const hasSponsor = sponsor !== null;

    const metaContent = { author, sponsor, date, placeholder, withoutAuthor, withoutTime };
    const meta = hasDocumentCardMeta(metaContent) ? (
        <DocumentCardMeta
            {...metaContent}
            withoutAuthorImage={withoutAuthorImage}
            className={styles.meta}
            authorClassName={styles.author}
            sponsorClassName={styles.sponsor}
            sponsorLabelClassName={styles.sponsorLabel}
            timeClassName={styles.time}
        />
    ) : null;

    return (
        <VerticalCard
            href={href || '#'}
            external={externalUrl !== null}
            onClick={!placeholder ? onClick : null}
            thumbnail={
                <DocumentCardThumbnail
                    type={type}
                    image={image}
                    coverImage={coverImage}
                    ratio={ratio}
                    duration={duration}
                    withoutDuration={withoutDuration}
                    withAdNotice={isNativeAd && !withoutAdNotice}
                    blocked={blocked && type !== 'micromag'}
                    unblocked={unblocked}
                    withIcon={!placeholder}
                    withoutBlockedIcon={withoutBlockedIcon}
                    withoutTypeIcon={withoutTypeIcon}
                    withBrandIcon={withBrandIcon}
                    brand={brandHandle}
                    size="medium"
                    width={width}
                    height={width !== null ? width / thumbnailRatio : null}
                    autoSize={width === null}
                    loading={imageLoading}
                    progress={progress}
                    withPlaceholder
                    className={styles.thumbnail}
                    iconClassName={styles.thumbnailIcon}
                    durationClassName={styles.duration}
                    adNoticeClassName={styles.adNotice}
                    withoutSize
                />
            }
            // height={isBox ? 'auto' : null}
            thumbnailRatio={thumbnailRatio}
            className={classNames([
                styles.container,
                {
                    [styles.isNativeAd]: isNativeAd,
                    [styles.withAdNotice]: !withoutAdNotice && isNativeAd,
                    [styles.withBorder]: withBorder && !isBox,
                    [styles.hasSponsor]: sponsor !== null,
                    [styles.hasSurtitle]: finalSurtitle !== null && showSurtitle,
                },
                className,
            ])}
            theme={theme || 'none'}
            size={size || 'default'}
            responsive={responsive}
            thumbnailClassName={classNames([styles.thumbnailContainer, thumbnailClassName])}
            innerClassName={styles.inner}
            labelClassName={classNames([
                styles.label,
                {
                    [styles.withTime]:
                        date !== null && !withoutTime && !hasSponsor && size === 'small',
                },
            ])}
        >
            {(category !== null || placeholder) && showCategory ? (
                <Category {...category} placeholder={placeholder} className={styles.category} />
            ) : null}
            {finalSurtitle !== null && showSurtitle ? (
                <Surtitle surtitle={finalSurtitle} className={styles.surtitle} />
            ) : null}
            <CardTitle className={styles.title} placeholder={placeholder} placeholderLines={3}>
                {title}
            </CardTitle>
            {!isSuggestion ? meta : null}
        </VerticalCard>
    );
};

export default memo(DocumentVerticalCard);
